/**
 * FP3-029 + FP3-029A — Reusable Student CSV Import page.
 *
 * Same component is mounted at two routes:
 *   /admin/import — admin/super_admin/org_admin view (full options)
 *   /import       — solo provider view (auto-assigns to self, no
 *                   org selector, no Invite-missing-providers opt-in,
 *                   no admin sub-nav)
 *
 * The component decides which surfaces to render off
 *   - user.permission_role
 *   - is_solo_provider (returned from /api/auth/me as a flag, also
 *     true when user.account_type=individual_provider)
 *
 * Steps:
 *   1. Upload → backend parses + validates, returns a preview batch
 *   2. Preview → review rows, opt in to school/invite creation
 *   3. Commit → backend writes the batch, returns result summary
 *
 * Recent Imports widget at the bottom lists up to the 5 most recent
 * batches scoped to the caller (own batches for solo / org for admin /
 * everywhere for super_admin). Sanitized; no per-row payload shown.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Upload,
  Download,
  CheckCircle,
  WarningCircle,
  XCircle,
  ArrowsClockwise,
  ClockClockwise,
  ArrowRight,
  CaretRight,
  Info,
  ListChecks,
  FloppyDisk,
  Trash,
  BookmarkSimple,
  PencilSimple,
} from "@phosphor-icons/react";
import axios from "axios";
import api, { prettyApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AdminShell from "./admin/AdminShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_STYLES = {
  create: { bg: "#E8F0EB", fg: "#1E3F33", label: "Create" },
  update: { bg: "#EEF1F6", fg: "#3F4756", label: "Update" },
  skip: { bg: "#F5F4ED", fg: "#6B7280", label: "Skip" },
  error: { bg: "#F8EFEA", fg: "#7A3A2C", label: "Error" },
  identity_conflict: {
    bg: "#F8EFEA",
    fg: "#7A3A2C",
    label: "Identity conflict — review required",
  },
};

const MATCH_REASON_LABELS = {
  uuid: "Frame UUID",
  frame_practice_id: "Frame Practice ID",
  external_id: "External ID",
  student_email: "Student email",
};

function ActionPill({ action }) {
  const s = ACTION_STYLES[action] || ACTION_STYLES.skip;
  return (
    <span
      data-testid={`row-action-${action}`}
      className="inline-flex items-center text-[10.5px] font-medium px-2 py-0.5 rounded-full"
      style={{ backgroundColor: s.bg, color: s.fg }}
    >
      {s.label}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

// FP3-076 — Canonical Frame Practice fields the user can map a school
// spreadsheet column to. Mirrors `routes.csv_import._ALIASES.keys()`.
// Grouped for the dropdown UX so a giant 38-item flat list doesn't
// overwhelm the user.
const CANONICAL_FIELD_GROUPS = [
  {
    label: "Student identity",
    options: [
      { value: "full_name", label: "Full name (Last, First or First Last)" },
      { value: "first_name", label: "First name" },
      { value: "last_name", label: "Last name" },
      { value: "external_id", label: "External / SIS ID" },
      { value: "student_uuid", label: "Frame UUID (internal id)" },
      { value: "frame_practice_id", label: "Frame Practice ID (FP-…)" },
      { value: "student_email", label: "Student email" },
      { value: "date_of_birth", label: "Date of birth" },
      { value: "sex", label: "Sex / gender" },
      { value: "grade", label: "Grade" },
    ],
  },
  {
    label: "Placement & schedule",
    options: [
      { value: "school_name", label: "School / campus" },
      { value: "attendance_pattern", label: "Attendance pattern" },
      { value: "lre", label: "LRE code" },
      { value: "status", label: "Service status" },
      { value: "teacher_name", label: "Teacher name" },
      { value: "teacher_email", label: "Teacher email" },
    ],
  },
  {
    label: "Provider routing",
    options: [
      { value: "case_manager", label: "Case Manager (auto-resolve)" },
      { value: "provider_email", label: "Assigned provider email" },
      { value: "provider_id", label: "Provider ID" },
      { value: "provider_specialty", label: "Provider specialty" },
    ],
  },
  {
    label: "Eligibility",
    options: [
      { value: "primary_eligibility", label: "Primary eligibility" },
      {
        value: "primary_eligibility_subcategories",
        label: "Primary subcategories",
      },
      { value: "secondary_eligibility", label: "Secondary eligibility" },
      {
        value: "secondary_eligibility_subcategories",
        label: "Secondary subcategories",
      },
      { value: "third_eligibility", label: "Tertiary eligibility" },
      {
        value: "third_eligibility_subcategories",
        label: "Tertiary subcategories",
      },
      { value: "fourth_eligibility", label: "Fourth eligibility" },
      {
        value: "fourth_eligibility_subcategories",
        label: "Fourth subcategories",
      },
    ],
  },
  {
    label: "Dates",
    options: [
      { value: "iep_due_date", label: "Annual IEP due date" },
      { value: "met_due_date", label: "Reevaluation / MET due date" },
      {
        value: "speech_re_eval_due_date",
        label: "Speech re-eval due date",
      },
      {
        value: "sped_need_service_start_date",
        label: "SPED service start date",
      },
      { value: "need_exit_date", label: "Anticipated exit date" },
      { value: "need_exit_reason", label: "Exit reason" },
    ],
  },
  {
    label: "Service info (text-only)",
    options: [
      { value: "service_domains", label: "Service domains" },
      {
        value: "service_obligation_minutes",
        label: "Service minutes (weekly/monthly)",
      },
      { value: "frequency_label", label: "Frequency / cadence" },
      {
        value: "related_service_text",
        label: "Related service (text only)",
      },
      { value: "services_text", label: "Services (text only)" },
      { value: "notes", label: "Notes" },
    ],
  },
];

const CANONICAL_FIELD_VALUES = new Set(
  CANONICAL_FIELD_GROUPS.flatMap((g) => g.options.map((o) => o.value))
);

// Header-slug aliases (mirrors `csv_import._slug_column` behaviour)
// so the mapping UI suggests sensible defaults for raw headers like
// "Last, First" → first_name. We're conservative — when in doubt we
// leave the cell unmapped and let the user pick.
const HEADER_ALIAS_MAP = {
  external_id: "external_id",
  student_id: "external_id",
  local_id: "external_id",
  sis_id: "external_id",
  external_student_id: "external_id",
  student_id_number: "external_id",
  student_id_no: "external_id",
  student_uuid: "student_uuid",
  uuid: "student_uuid",
  frame_uuid: "student_uuid",
  internal_id: "student_uuid",
  frame_practice_id: "frame_practice_id",
  readable_id: "frame_practice_id",
  frame_id: "frame_practice_id",
  fp_id: "frame_practice_id",
  frame_practice_student_id: "frame_practice_id",
  first_name: "first_name",
  first: "first_name",
  student_first_name: "first_name",
  given_name: "first_name",
  last_name: "last_name",
  last: "last_name",
  student_last_name: "last_name",
  surname: "last_name",
  family_name: "last_name",
  full_name: "full_name",
  fullname: "full_name",
  student: "full_name",
  student_name: "full_name",
  name: "full_name",
  pupil_name: "full_name",
  grade: "grade",
  grade_level: "grade",
  school_name: "school_name",
  school: "school_name",
  campus: "school_name",
  provider_email: "provider_email",
  assigned_provider_email: "provider_email",
  provider_id: "provider_id",
  case_manager: "case_manager",
  case_manager_name: "case_manager",
  case_managers: "case_manager",
  teacher: "teacher_name",
  teacher_name: "teacher_name",
  homeroom_teacher: "teacher_name",
  gen_ed_teacher: "teacher_name",
  general_education_teacher: "teacher_name",
  teacher_email: "teacher_email",
  status: "status",
  student_email: "student_email",
  student_e_mail: "student_email",
  pupil_email: "student_email",
  date_of_birth: "date_of_birth",
  dob: "date_of_birth",
  birthdate: "date_of_birth",
  birth_date: "date_of_birth",
  sex: "sex",
  gender: "sex",
  primary_eligibility: "primary_eligibility",
  eligibility: "primary_eligibility",
  eligibility_1: "primary_eligibility",
  eligibility_primary: "primary_eligibility",
  primary_category: "primary_eligibility",
  primary_disability: "primary_eligibility",
  primary_eligibility_subcategories: "primary_eligibility_subcategories",
  eligibility_1_subcategories: "primary_eligibility_subcategories",
  primary_eligibility_areas: "primary_eligibility_subcategories",
  secondary_eligibility: "secondary_eligibility",
  eligibility_2: "secondary_eligibility",
  eligibility_secondary: "secondary_eligibility",
  secondary_category: "secondary_eligibility",
  secondary_eligibility_subcategories: "secondary_eligibility_subcategories",
  eligibility_2_subcategories: "secondary_eligibility_subcategories",
  third_eligibility: "third_eligibility",
  eligibility_3: "third_eligibility",
  additional_eligibility_1: "third_eligibility",
  tertiary_eligibility: "third_eligibility",
  tertiary_category: "third_eligibility",
  third_eligibility_subcategories: "third_eligibility_subcategories",
  eligibility_3_subcategories: "third_eligibility_subcategories",
  tertiary_eligibility_subcategories: "third_eligibility_subcategories",
  fourth_eligibility: "fourth_eligibility",
  eligibility_4: "fourth_eligibility",
  additional_eligibility_2: "fourth_eligibility",
  fourth_eligibility_subcategories: "fourth_eligibility_subcategories",
  eligibility_4_subcategories: "fourth_eligibility_subcategories",
  lre: "lre",
  lre_level: "lre",
  least_restrictive_environment: "lre",
  lre_type: "lre",
  iep_due_date: "iep_due_date",
  iep_date: "iep_due_date",
  iep_due: "iep_due_date",
  annual_iep_due_date: "iep_due_date",
  met_due_date: "met_due_date",
  met_evaluation_due_date: "met_due_date",
  met_date: "met_due_date",
  evaluation_due_date: "met_due_date",
  reevaluation_due_date: "met_due_date",
  "3_year_re_eval": "met_due_date",
  three_year_re_eval: "met_due_date",
  triennial_reevaluation: "met_due_date",
  service_domains: "service_domains",
  service_areas_csv: "service_domains",
  domains: "service_domains",
  service_domain: "service_domains",
  attendance_pattern: "attendance_pattern",
  attendance: "attendance_pattern",
  sped_need_service_start_date: "sped_need_service_start_date",
  sped_start_date: "sped_need_service_start_date",
  service_start_date: "sped_need_service_start_date",
  sped_need_service_start: "sped_need_service_start_date",
  need_exit_date: "need_exit_date",
  exit_date: "need_exit_date",
  anticipated_exit_date: "need_exit_date",
  need_exit_reason: "need_exit_reason",
  exit_reason: "need_exit_reason",
  reason: "need_exit_reason",
  speech_re_eval_due_date: "speech_re_eval_due_date",
  speech_re_eval: "speech_re_eval_due_date",
  "3_year_speech_re_eval": "speech_re_eval_due_date",
  three_year_speech_re_eval: "speech_re_eval_due_date",
  speech_reevaluation_due_date: "speech_re_eval_due_date",
  related_service_text: "related_service_text",
  related_service: "related_service_text",
  related_services: "related_service_text",
  services_text: "services_text",
  services: "services_text",
  notes: "notes",
  import_notes: "notes",
  service_obligation_minutes: "service_obligation_minutes",
  service_minutes: "service_obligation_minutes",
  minutes_per_week: "service_obligation_minutes",
  weekly_minutes: "service_obligation_minutes",
  minutes_per_month: "service_obligation_minutes",
  monthly_minutes: "service_obligation_minutes",
  frequency_label: "frequency_label",
  frequency: "frequency_label",
  delivery_frequency: "frequency_label",
  sessions_per_month: "frequency_label",
  sessions_per_week: "frequency_label",
};

// Slugify a raw spreadsheet header the same way the backend does in
// `_slug_column` so client-side auto-detection stays in lockstep.
function slugifyHeader(raw) {
  return String(raw || "")
    .replace(/\ufeff/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

// Best-effort CSV header-row parser (only the first line). Quoted
// values supported. We never store the body — it lives in the File
// object only and is sent to the backend as multipart on Preview.
function parseCsvHeaderRow(text) {
  if (!text) return [];
  const firstLine = text.split(/\r?\n/, 1)[0] || "";
  const out = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < firstLine.length; i += 1) {
    const ch = firstLine[i];
    if (inQuotes) {
      if (ch === '"' && firstLine[i + 1] === '"') {
        cur += '"';
        i += 1;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        cur += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      out.push(cur.trim());
      cur = "";
    } else {
      cur += ch;
    }
  }
  out.push(cur.trim());
  return out.filter((h) => h.length > 0);
}

function autoMapHeader(raw) {
  const slug = slugifyHeader(raw);
  return HEADER_ALIAS_MAP[slug] || "";
}

export default function ImportStudentsPage({ mode = "admin" }) {
  // mode is informational; the actual capability gate is the user role.
  const { user, organization, ensureStudentDataAck } = useAuth();
  const [meExtras, setMeExtras] = useState({ is_solo_provider: false });

  const role = user?.permission_role;
  const isSuperAdmin = role === "super_admin";
  const isAdmin = ["admin", "org_admin", "super_admin"].includes(role || "");
  const isProvider = role === "provider";
  const isSoloProvider = isProvider && !!meExtras.is_solo_provider;
  const canImport = isAdmin || isSoloProvider;

  // FP3-056 — Solo provider workspace introspection.
  //   soloSchoolCount === 0  → block import, prompt to add a workspace.
  //   soloSchoolCount === 1  → default rows to that school; campus optional.
  //   soloSchoolCount  > 1   → campus column conditionally required per row.
  // Admins are unconstrained.
  const soloSchoolIds = useMemo(
    () => (user?.school_ids || []).filter(Boolean),
    [user]
  );
  const soloSchoolCount = isSoloProvider ? soloSchoolIds.length : null;
  const soloHasNoWorkspace = isSoloProvider && soloSchoolCount === 0;
  const soloRequiresCampus = isSoloProvider && soloSchoolCount > 1;

  const [organizations, setOrganizations] = useState([]);
  const [orgId, setOrgId] = useState(user?.organization_id || "");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [opts, setOpts] = useState({
    allow_create_schools: false,
    invite_missing_providers: false,
    allow_partial_import: false,
  });
  const [recentBatches, setRecentBatches] = useState([]);
  // FP3-076 — Saved import profiles + per-upload mapping state.
  const [profiles, setProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState("");
  const [orgSchools, setOrgSchools] = useState([]);
  const [defaultSchoolId, setDefaultSchoolId] = useState("");
  const [rawHeaders, setRawHeaders] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [saveProfileMode, setSaveProfileMode] = useState(false);
  const [saveProfileName, setSaveProfileName] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    // Pick up the is_solo_provider flag the backend exposes. We don't
    // rely on the local AuthProvider for this since the field was
    // added in FP3-029A and may not exist on older sessions.
    api
      .authMe()
      .then((r) => setMeExtras({ is_solo_provider: !!r.data?.is_solo_provider }))
      .catch(() => setMeExtras({ is_solo_provider: false }));
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    api
      .getOrganizations()
      .then((r) => setOrganizations(r.data || []))
      .catch(() => setOrganizations([]));
  }, [isAdmin]);

  const loadRecent = useCallback(() => {
    if (!canImport) return;
    api
      .listCsvImportBatches({ limit: 5 })
      .then((r) => setRecentBatches(r.data || []))
      .catch(() => setRecentBatches([]));
  }, [canImport]);

  useEffect(() => {
    loadRecent();
  }, [loadRecent]);

  // FP3-076 — Load saved import profiles + reachable schools.
  const loadProfiles = useCallback(() => {
    if (!canImport) return;
    api
      .listImportProfiles()
      .then((r) => setProfiles(r.data || []))
      .catch(() => setProfiles([]));
  }, [canImport]);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  useEffect(() => {
    if (!canImport) return;
    // Solo providers see only their own schools; admins see all org
    // schools. listSchools returns the caller-scoped set, so the same
    // endpoint works for both.
    api
      .listSchools()
      .then((r) => setOrgSchools((r.data || []).filter((s) => !s.is_archived)))
      .catch(() => setOrgSchools([]));
  }, [canImport]);

  // FP3-076 — Parse the chosen file's header row once and seed an
  // initial column mapping using the alias map (so the user only has
  // to fix unmapped headers instead of mapping all of them).
  const handleFileChange = (newFile) => {
    setFile(newFile);
    setRawHeaders([]);
    setColumnMapping({});
    setSaveProfileMode(false);
    setSaveProfileName("");
    if (!newFile) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = String(ev.target?.result || "");
      const headers = parseCsvHeaderRow(text);
      setRawHeaders(headers);
      // Seed mapping from auto-detect; if a profile is selected, its
      // mapping wins over auto-detect for matched headers.
      const seeded = {};
      const profile = profiles.find((p) => p.id === selectedProfileId);
      const profileMap = profile?.column_mapping || {};
      for (const h of headers) {
        if (profileMap[h] && CANONICAL_FIELD_VALUES.has(profileMap[h])) {
          seeded[h] = profileMap[h];
        } else {
          const auto = autoMapHeader(h);
          if (auto) seeded[h] = auto;
        }
      }
      setColumnMapping(seeded);
    };
    reader.readAsText(newFile);
  };

  // FP3-076 — Apply a saved profile: pull its mapping + default
  // school. If a file is already chosen, re-seed the mapping using
  // the profile values; otherwise just hold them until the user
  // picks a file.
  const applyProfile = (profileId) => {
    setSelectedProfileId(profileId);
    if (!profileId) {
      setColumnMapping({});
      setDefaultSchoolId("");
      return;
    }
    const profile = profiles.find((p) => p.id === profileId);
    if (!profile) return;
    setDefaultSchoolId(profile.default_school_id || "");
    if (rawHeaders.length === 0) {
      // Still useful — once the file is picked, handleFileChange
      // will pick up the selected profile and seed from it.
      setColumnMapping(profile.column_mapping || {});
      return;
    }
    const next = {};
    const map = profile.column_mapping || {};
    for (const h of rawHeaders) {
      if (map[h] && CANONICAL_FIELD_VALUES.has(map[h])) {
        next[h] = map[h];
      } else {
        const auto = autoMapHeader(h);
        if (auto) next[h] = auto;
      }
    }
    setColumnMapping(next);
  };

  const setMappingFor = (header, value) => {
    setColumnMapping((prev) => {
      const next = { ...prev };
      if (!value) {
        delete next[header];
      } else {
        next[header] = value;
      }
      return next;
    });
  };

  const cleanMappingForSubmit = useCallback(() => {
    const out = {};
    for (const [h, v] of Object.entries(columnMapping)) {
      if (v && CANONICAL_FIELD_VALUES.has(v)) {
        out[h] = v;
      }
    }
    return out;
  }, [columnMapping]);

  const saveCurrentProfile = async () => {
    const name = saveProfileName.trim();
    if (!name) {
      toast.error("Give the profile a name first.");
      return;
    }
    const mapping = cleanMappingForSubmit();
    if (Object.keys(mapping).length === 0) {
      toast.error("Map at least one column before saving the profile.");
      return;
    }
    try {
      await api.createImportProfile({
        name,
        column_mapping: mapping,
        default_school_id: defaultSchoolId || "",
      });
      toast.success(`Saved profile “${name}”.`);
      setSaveProfileMode(false);
      setSaveProfileName("");
      loadProfiles();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save profile.");
    }
  };

  const overwriteCurrentProfile = async () => {
    if (!selectedProfileId) return;
    const mapping = cleanMappingForSubmit();
    try {
      await api.updateImportProfile(selectedProfileId, {
        column_mapping: mapping,
        default_school_id: defaultSchoolId || "",
      });
      toast.success("Profile updated.");
      loadProfiles();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not update profile.");
    }
  };

  const deleteCurrentProfile = async () => {
    if (!selectedProfileId) return;
    const profile = profiles.find((p) => p.id === selectedProfileId);
    const name = profile?.name || "this profile";
    if (!window.confirm(`Delete profile “${name}”? This cannot be undone.`)) return;
    try {
      await api.deleteImportProfile(selectedProfileId);
      toast.success(`Deleted “${name}”.`);
      setSelectedProfileId("");
      setDefaultSchoolId("");
      loadProfiles();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not delete profile.");
    }
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setError("");
    setRawHeaders([]);
    setColumnMapping({});
    setSaveProfileMode(false);
    setSaveProfileName("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const downloadTemplate = async () => {
    try {
      const r = await axios.get(`${API_URL}/students/import-csv/template`, {
        withCredentials: true,
      });
      const blob = new Blob([r.data.csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "frame-practice-student-import-template.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download template");
    }
  };

  const doPreview = async (e) => {
    e?.preventDefault();
    setError("");
    setResult(null);
    if (!file) {
      setError("Choose a CSV file to preview.");
      return;
    }
    // FP3-078 — Block on the student-data authorization checkpoint
    // before the CSV import preview fires. Already-acknowledged
    // users pass straight through; everyone else sees the one-time
    // modal first.
    const ok = await ensureStudentDataAck();
    if (!ok) return;
    const form = new FormData();
    form.append("file", file);
    if (isSuperAdmin && orgId) form.append("organization_id", orgId);
    // FP3-076 — column mapping + default school + selected profile
    // metadata. All optional; backend falls back to Frame default
    // alias detection if mapping is empty.
    const mapping = cleanMappingForSubmit();
    if (Object.keys(mapping).length > 0) {
      form.append("column_mapping", JSON.stringify(mapping));
    }
    if (defaultSchoolId) form.append("default_school_id", defaultSchoolId);
    if (selectedProfileId) form.append("profile_id", selectedProfileId);
    try {
      const r = await axios.post(
        `${API_URL}/students/import-csv/preview`,
        form,
        { withCredentials: true }
      );
      setPreview(r.data);
    } catch (err) {
      setError(prettyApiError(err, "Could not preview the CSV."));
    }
  };

  const doCommit = async () => {
    if (!preview) return;
    setCommitting(true);
    setError("");
    try {
      const submitOpts = { ...opts };
      if (isSoloProvider) {
        // Solo providers can't invite missing providers from CSV; the
        // backend ignores the flag too, but we don't even send it.
        submitOpts.invite_missing_providers = false;
      }
      const r = await axios.post(
        `${API_URL}/students/import-csv/commit`,
        { batch_id: preview.id, ...submitOpts },
        { withCredentials: true }
      );
      setResult(r.data.result);
      loadRecent();
      toast.success("Import complete");
    } catch (err) {
      setError(prettyApiError(err, "Could not commit the import."));
    } finally {
      setCommitting(false);
    }
  };

  const hasErrors = (preview?.summary?.error_rows || 0) > 0;
  const hasIdentityConflicts =
    (preview?.summary?.identity_conflict_rows || 0) > 0;
  const commitBlocked =
    (hasErrors || hasIdentityConflicts) && !opts.allow_partial_import;
  // Solo providers never see the org selector; the backend pins org_id
  // to their own.
  const orgSelectorVisible = isSuperAdmin;

  const pageTitle = isSoloProvider
    ? "Import My Caseload"
    : "CSV Import · Students";
  const pageSubtitle = isSoloProvider
    ? "Bulk-load your own students into Frame Practice. Imported rows are auto-assigned to you."
    : "Bulk-load students into Frame Practice. Preview first, then commit.";

  if (!canImport) {
    const inner = (
      <Card className="border-[#E5E3DB] rounded-2xl">
        <CardContent
          data-testid="csv-import-forbidden"
          className="p-6 text-center text-[#6B7280] text-[13px]"
        >
          You don't have permission to view this page.
        </CardContent>
      </Card>
    );
    return isSoloProvider ? (
      <SoloShell title={pageTitle} subtitle={pageSubtitle}>
        {inner}
      </SoloShell>
    ) : (
      <AdminShell title={pageTitle} subtitle={pageSubtitle}>
        {inner}
      </AdminShell>
    );
  }

  const body = (
    <>
      {/* Solo provider banner */}
      {isSoloProvider && (
        <Card
          data-testid="solo-import-banner"
          className="border-[#E5E3DB] rounded-2xl mb-4"
        >
          <CardContent className="p-4 text-[12.5px] text-[#1F2937]">
            <span className="font-semibold text-[#1E3F33]">
              Solo provider import.
            </span>{" "}
            Every imported student will be assigned to you. Provider columns in
            the CSV are optional — if filled, they must match your own
            profile. You cannot invite other providers from this page.
          </CardContent>
        </Card>
      )}

      {/* FP3-056 — No-workspace blocker. Solo provider with zero
          school/workspace assignments cannot import into a blank
          campus state. Surface a clear, actionable card and hide
          the upload UI entirely. */}
      {!preview && !result && soloHasNoWorkspace && (
        <Card
          data-testid="solo-import-no-workspace"
          className="border-[#E8D6CD] bg-[#FBEFE8] rounded-2xl mb-4"
        >
          <CardContent className="p-5 flex items-start gap-3">
            <WarningCircle
              size={22}
              weight="duotone"
              className="text-[#A8453A] shrink-0 mt-0.5"
            />
            <div className="text-[13px] text-[#7A3A2C] leading-relaxed">
              <p className="font-semibold mb-0.5">
                Add a school/workspace before importing students.
              </p>
              <p className="text-[#9B6052]">
                Your account isn't assigned to a school or workspace yet, so
                imported students would land in a blank campus state. Add a
                school in your workspace settings first, then come back to
                bulk-load your caseload.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* FP3-056 — CSV column requirements panel. Renders above the
          upload card on every variant so the required / conditional /
          optional split is obvious before the user picks a file. */}
      {!preview && !result && !soloHasNoWorkspace && (
        <CsvColumnGuide
          isSoloProvider={isSoloProvider}
          soloRequiresCampus={soloRequiresCampus}
          onDownloadTemplate={downloadTemplate}
        />
      )}

      {/* Step 1 / Upload */}
      {!preview && !result && !soloHasNoWorkspace && (
        <Card
          data-testid="csv-import-upload-card"
          className="border-[#E5E3DB] rounded-2xl mb-6"
        >
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-[14px] font-semibold text-[#1F2937] heading-font tracking-tight">
                Upload CSV
              </h2>
              <Button
                data-testid="csv-template-download"
                onClick={downloadTemplate}
                variant="outline"
                className="h-8 text-[12px] border-[#E5E3DB] text-[#1E3F33] hover:bg-[#E8F0EB]"
              >
                <Download size={12} className="mr-1.5" />
                Template
              </Button>
            </div>

            {orgSelectorVisible && (
              <div>
                <Label className="text-[11.5px] uppercase tracking-[0.1em] text-[#6B7280] font-semibold">
                  Organization
                </Label>
                <select
                  data-testid="csv-org-select"
                  value={orgId}
                  onChange={(e) => setOrgId(e.target.value)}
                  className="mt-1.5 block w-full h-10 border border-[#E5E3DB] rounded-md px-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1E3F33]/30 focus:border-[#1E3F33]"
                >
                  <option value="">— Select organization —</option>
                  {organizations.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {!orgSelectorVisible && !isSoloProvider && (
              <div className="text-[12px] text-[#6B7280] bg-[#F5F4ED] border border-[#E5E3DB] rounded-md px-3 py-2">
                Importing into your organization (
                {organizations[0]?.name || "current organization"}).
              </div>
            )}
            {isSoloProvider && (
              <div
                data-testid="solo-org-notice"
                className="text-[12px] text-[#6B7280] bg-[#F5F4ED] border border-[#E5E3DB] rounded-md px-3 py-2"
              >
                Importing into your practice
                {organization?.name ? ` (${organization.name})` : ""}.
              </div>
            )}

            <div>
              <Label className="text-[11.5px] uppercase tracking-[0.1em] text-[#6B7280] font-semibold">
                CSV file
              </Label>
              <Input
                ref={fileInputRef}
                data-testid="csv-file-input"
                type="file"
                accept=".csv"
                onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
                className="mt-1.5 border-[#E5E3DB] bg-white"
              />
              <p className="text-[11px] text-[#9AA0A6] mt-1.5">
                Up to 500 rows. UTF-8 encoded. Required columns:{" "}
                <code>first_name</code>, <code>last_name</code>,{" "}
                <code>grade</code>. See the CSV column guide above for the
                full list — extra supported columns are optional and can be
                left blank.
                {isSoloProvider && soloRequiresCampus && (
                  <>
                    {" "}
                    <span
                      data-testid="solo-multi-school-notice"
                      className="text-[#7A6A2C]"
                    >
                      Because you have {soloSchoolCount} schools, a{" "}
                      <code>campus</code> column is required per row so
                      students land at the right site.
                    </span>
                  </>
                )}
                {/* FP3-064 — Reassure single-school solo providers that
                    blank campus rows will land at their workspace, not
                    in an unassigned limbo. Mirrors the backend
                    auto-default in csv_import.py. */}
                {isSoloProvider && soloSchoolCount === 1 && (
                  <>
                    {" "}
                    <span
                      data-testid="solo-default-campus-notice"
                      className="text-[#1E3F33]"
                    >
                      Rows with blank <code>campus</code> will default to
                      your workspace automatically.
                    </span>
                  </>
                )}
              </p>
            </div>

            {/* FP3-076 — Import Profile + Column Mapping panel.
                Renders only after a file is chosen so the user has
                concrete headers to map. Hidden until then to keep
                the upload card calm. */}
            {file && (
              <ImportProfilePanel
                profiles={profiles}
                selectedProfileId={selectedProfileId}
                onSelectProfile={applyProfile}
                onDeleteProfile={deleteCurrentProfile}
                onOverwriteProfile={overwriteCurrentProfile}
                saveProfileMode={saveProfileMode}
                onToggleSaveMode={() => {
                  setSaveProfileMode((v) => !v);
                  setSaveProfileName("");
                }}
                saveProfileName={saveProfileName}
                onChangeSaveName={setSaveProfileName}
                onSaveProfile={saveCurrentProfile}
                rawHeaders={rawHeaders}
                columnMapping={columnMapping}
                onSetMapping={setMappingFor}
                orgSchools={orgSchools}
                defaultSchoolId={defaultSchoolId}
                onChangeDefaultSchool={setDefaultSchoolId}
                soloSchoolCount={soloSchoolCount}
              />
            )}

            <div className="flex flex-wrap gap-3 pt-2 border-t border-[#F1F2F4]">
              <OptCheckbox
                testId="opt-create-schools"
                checked={opts.allow_create_schools}
                onChange={(v) =>
                  setOpts((o) => ({ ...o, allow_create_schools: v }))
                }
              >
                Allow new schools / campuses
              </OptCheckbox>
              {!isSoloProvider && (
                <OptCheckbox
                  testId="opt-invite-providers"
                  checked={opts.invite_missing_providers}
                  onChange={(v) =>
                    setOpts((o) => ({ ...o, invite_missing_providers: v }))
                  }
                >
                  Invite providers from <code>provider_email</code> column
                </OptCheckbox>
              )}
              <OptCheckbox
                testId="opt-partial-import"
                checked={opts.allow_partial_import}
                onChange={(v) =>
                  setOpts((o) => ({ ...o, allow_partial_import: v }))
                }
              >
                Allow partial import (skip invalid rows)
              </OptCheckbox>
            </div>

            {error && (
              <div
                data-testid="csv-error"
                className="rounded-md border border-[#E8D6CD] bg-[#F8EFEA] text-[#7A3A2C] text-[12.5px] px-3 py-2"
              >
                {error}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                data-testid="csv-preview-submit"
                onClick={doPreview}
                disabled={!file || (orgSelectorVisible && !orgId)}
                className="h-9 px-4 bg-[#1E3F33] hover:bg-[#173127] disabled:bg-[#1E3F33]/60 text-white text-[13px]"
              >
                <Upload size={12} className="mr-1.5" />
                Preview import
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2 / Preview */}
      {preview && !result && (
        <PreviewView
          preview={preview}
          opts={opts}
          setOpts={setOpts}
          onBack={reset}
          onCommit={doCommit}
          committing={committing}
          commitBlocked={commitBlocked}
          error={error}
          isSoloProvider={isSoloProvider}
        />
      )}

      {/* Step 3 / Result */}
      {result && <ResultView result={result} onAnother={reset} isSoloProvider={isSoloProvider} />}

      {/* Recent Imports widget */}
      <RecentImports batches={recentBatches} onRefresh={loadRecent} />
    </>
  );

  return isSoloProvider ? (
    <SoloShell title={pageTitle} subtitle={pageSubtitle}>
      {body}
    </SoloShell>
  ) : (
    <AdminShell title={pageTitle} subtitle={pageSubtitle}>
      {body}
    </AdminShell>
  );
}

function SoloShell({ children, title, subtitle }) {
  return (
    <div
      data-testid="solo-import-shell"
      className="p-6 max-w-7xl mx-auto"
    >
      <div className="mb-5">
        <h1
          data-testid="solo-page-title"
          className="text-2xl font-semibold text-[#1F2937] heading-font tracking-tight"
        >
          {title}
        </h1>
        {subtitle && (
          <p className="text-[12px] text-[#6B7280] mt-1">{subtitle}</p>
        )}
      </div>
      {children}
    </div>
  );
}

function OptCheckbox({ testId, checked, onChange, children }) {
  return (
    <label className="inline-flex items-center gap-2 text-[12.5px] text-[#1F2937] cursor-pointer">
      <input
        data-testid={testId}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="rounded border-[#E5E3DB] accent-[#1E3F33]"
      />
      {children}
    </label>
  );
}

/**
 * FP3-076 — Import Profile + Column Mapping panel.
 *
 * Renders inside the upload card once a file is selected. Three
 * sections:
 *
 *   1. Saved profile picker (load / save / overwrite / delete).
 *   2. Default school picker (used when no school column is mapped).
 *   3. Header → Frame field mapping table.
 */
function ImportProfilePanel({
  profiles,
  selectedProfileId,
  onSelectProfile,
  onDeleteProfile,
  onOverwriteProfile,
  saveProfileMode,
  onToggleSaveMode,
  saveProfileName,
  onChangeSaveName,
  onSaveProfile,
  rawHeaders,
  columnMapping,
  onSetMapping,
  orgSchools,
  defaultSchoolId,
  onChangeDefaultSchool,
  soloSchoolCount,
}) {
  const mappedCount = Object.values(columnMapping || {}).filter((v) =>
    CANONICAL_FIELD_VALUES.has(v)
  ).length;
  const requiredCovered = (() => {
    const mapped = new Set(Object.values(columnMapping || {}));
    const fullName = mapped.has("full_name");
    const firstLast = mapped.has("first_name") && mapped.has("last_name");
    return {
      name: fullName || firstLast,
      grade: mapped.has("grade"),
    };
  })();

  return (
    <div
      data-testid="import-profile-panel"
      className="rounded-lg border border-[#E5E3DB] bg-[#FBFAF6] p-4 space-y-4"
    >
      <div className="flex items-start gap-2">
        <BookmarkSimple
          size={18}
          weight="duotone"
          className="text-[#1E3F33] shrink-0 mt-0.5"
        />
        <div>
          <p className="text-[13px] font-semibold text-[#1F2937] heading-font">
            Map this spreadsheet to Frame fields
          </p>
          <p className="text-[11.5px] text-[#6B7280] mt-0.5">
            Pick a saved profile or map columns manually. The Frame
            default template still works automatically when columns
            already match.
          </p>
        </div>
      </div>

      {/* Profile picker */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 items-end">
        <div>
          <Label className="text-[10.5px] uppercase tracking-[0.08em] text-[#6B7280] font-semibold">
            Saved profile
          </Label>
          <select
            data-testid="import-profile-select"
            value={selectedProfileId}
            onChange={(e) => onSelectProfile(e.target.value)}
            className="mt-1 block w-full h-9 border border-[#E5E3DB] rounded-md px-2 text-[12.5px] bg-white focus:outline-none focus:ring-2 focus:ring-[#1E3F33]/30 focus:border-[#1E3F33]"
          >
            <option value="">— None (manual mapping) —</option>
            {profiles.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex gap-1.5">
          {selectedProfileId && (
            <>
              <Button
                data-testid="import-profile-overwrite"
                type="button"
                onClick={onOverwriteProfile}
                variant="outline"
                className="h-9 px-2.5 text-[11.5px] border-[#E5E3DB] text-[#1E3F33] hover:bg-[#E8F0EB]"
                title="Save changes back to this profile"
              >
                <PencilSimple size={11} className="mr-1" />
                Update
              </Button>
              <Button
                data-testid="import-profile-delete"
                type="button"
                onClick={onDeleteProfile}
                variant="outline"
                className="h-9 px-2.5 text-[11.5px] border-[#E8D6CD] text-[#7A3A2C] hover:bg-[#F8EFEA]"
                title="Delete this profile"
              >
                <Trash size={11} className="mr-1" />
                Delete
              </Button>
            </>
          )}
          <Button
            data-testid="import-profile-save-toggle"
            type="button"
            onClick={onToggleSaveMode}
            variant="outline"
            className="h-9 px-2.5 text-[11.5px] border-[#E5E3DB] text-[#1E3F33] hover:bg-[#E8F0EB]"
          >
            <FloppyDisk size={11} className="mr-1" />
            {saveProfileMode ? "Cancel save" : "Save as new"}
          </Button>
        </div>
      </div>

      {saveProfileMode && (
        <div
          data-testid="import-profile-save-form"
          className="rounded-md bg-white border border-[#E5E3DB] p-3 flex flex-wrap items-end gap-2"
        >
          <div className="flex-1 min-w-[200px]">
            <Label className="text-[10.5px] uppercase tracking-[0.08em] text-[#6B7280] font-semibold">
              Profile name
            </Label>
            <Input
              data-testid="import-profile-save-name"
              value={saveProfileName}
              onChange={(e) => onChangeSaveName(e.target.value)}
              placeholder="e.g. Lincoln Elementary export"
              className="mt-1 h-9 text-[12.5px] border-[#E5E3DB] bg-white"
            />
          </div>
          <Button
            data-testid="import-profile-save-confirm"
            type="button"
            onClick={onSaveProfile}
            className="h-9 px-3 bg-[#1E3F33] hover:bg-[#173127] text-white text-[12.5px]"
          >
            <FloppyDisk size={12} className="mr-1.5" />
            Save profile
          </Button>
        </div>
      )}

      {/* Default school picker */}
      {orgSchools.length > 0 && (
        <div>
          <Label className="text-[10.5px] uppercase tracking-[0.08em] text-[#6B7280] font-semibold">
            Default school for rows without one
          </Label>
          <select
            data-testid="import-default-school"
            value={defaultSchoolId}
            onChange={(e) => onChangeDefaultSchool(e.target.value)}
            className="mt-1 block w-full h-9 border border-[#E5E3DB] rounded-md px-2 text-[12.5px] bg-white focus:outline-none focus:ring-2 focus:ring-[#1E3F33]/30 focus:border-[#1E3F33]"
          >
            <option value="">— No default (use mapped school column) —</option>
            {orgSchools.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <p className="text-[10.5px] text-[#6B7280] mt-1">
            Rows that don&apos;t map a <code>School</code> column will land at
            this campus. Leave empty if the spreadsheet already contains
            school names.
            {soloSchoolCount === 1 && (
              <span className="ml-1 text-[#1E3F33]">
                Single-workspace shortcut — pick your workspace here once,
                save the profile, and never re-pick it.
              </span>
            )}
          </p>
        </div>
      )}

      {/* Mapping grid */}
      <div data-testid="import-mapping-grid">
        <div className="flex items-center justify-between mb-1.5">
          <Label className="text-[10.5px] uppercase tracking-[0.08em] text-[#6B7280] font-semibold">
            Column mapping ({mappedCount}/{rawHeaders.length} mapped)
          </Label>
          {(!requiredCovered.name || !requiredCovered.grade) && (
            <span className="text-[10.5px] text-[#7A6A2C] bg-[#F5EFE3] border border-[#E8DCB8] rounded-full px-2 py-0.5">
              Map a name column (or full_name) + grade before commit.
            </span>
          )}
        </div>
        <div className="rounded-md border border-[#E5E3DB] overflow-hidden bg-white">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="border-b border-[#F1F2F4] text-[10.5px] uppercase tracking-[0.08em] text-[#6B7280] font-semibold">
                <th className="text-left px-3 py-2 w-[45%]">Your spreadsheet column</th>
                <th className="text-left px-3 py-2">Frame field</th>
              </tr>
            </thead>
            <tbody>
              {rawHeaders.map((h) => (
                <tr
                  key={h}
                  data-testid={`import-mapping-row-${h}`}
                  className="border-b border-[#F8F7F2] last:border-b-0"
                >
                  <td className="px-3 py-1.5">
                    <code className="text-[11.5px] text-[#1F2937] bg-[#F5F4ED] px-1.5 py-0.5 rounded">
                      {h}
                    </code>
                  </td>
                  <td className="px-3 py-1.5">
                    <select
                      data-testid={`import-mapping-select-${slugifyHeader(h)}`}
                      value={columnMapping[h] || ""}
                      onChange={(e) => onSetMapping(h, e.target.value)}
                      className="block w-full h-8 border border-[#E5E3DB] rounded-md px-2 text-[12px] bg-white focus:outline-none focus:ring-2 focus:ring-[#1E3F33]/30 focus:border-[#1E3F33]"
                    >
                      <option value="">— Skip / ignore —</option>
                      {CANONICAL_FIELD_GROUPS.map((g) => (
                        <optgroup key={g.label} label={g.label}>
                          {g.options.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
              {rawHeaders.length === 0 && (
                <tr>
                  <td
                    colSpan={2}
                    className="px-3 py-3 text-center text-[#9AA0A6] text-[12px]"
                  >
                    No headers detected yet. Make sure the first CSV row
                    is the header line.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PreviewView({
  preview,
  opts,
  setOpts,
  onBack,
  onCommit,
  committing,
  commitBlocked,
  error,
  isSoloProvider,
}) {
  const s = preview.summary || {};
  return (
    <>
      <Card className="border-[#E5E3DB] rounded-2xl mb-6">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-[14px] font-semibold text-[#1F2937] heading-font">
              Preview — {preview.filename}
            </h2>
            <Button
              onClick={onBack}
              variant="outline"
              className="h-8 text-[12px] border-[#E5E3DB] text-[#6B7280]"
            >
              Back
            </Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[12px]">
            <SummaryTile label="Total rows" value={s.total_rows} />
            <SummaryTile label="Valid" value={s.valid_rows} tone="ok" />
            <SummaryTile label="Warnings" value={s.warning_rows} tone="warn" />
            <SummaryTile label="Errors" value={s.error_rows} tone="error" />
            <SummaryTile label="Will create" value={s.create_rows} />
            <SummaryTile label="Will update" value={s.update_rows} />
            {(s.identity_conflict_rows || 0) > 0 && (
              <SummaryTile
                label="Identity conflicts"
                value={s.identity_conflict_rows}
                tone="error"
              />
            )}
            <SummaryTile label="New schools" value={(s.new_schools || []).length} />
            <SummaryTile
              label="Missing providers"
              value={(s.missing_provider_emails || []).length}
            />
          </div>
          {preview.unmapped_headers?.length > 0 && (
            <div className="text-[11.5px] text-[#6B7280]">
              <strong>Unmapped columns:</strong>{" "}
              {preview.unmapped_headers.join(", ")}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-[#E5E3DB] rounded-2xl overflow-hidden mb-6">
        <CardContent className="p-0">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="border-b border-[#F1F2F4] text-[10.5px] uppercase tracking-[0.1em] text-[#6B7280] font-semibold">
                <th className="text-left px-3 py-2.5">#</th>
                <th className="text-left px-3 py-2.5">Student</th>
                <th className="text-left px-3 py-2.5">Grade</th>
                <th className="text-left px-3 py-2.5">School</th>
                <th className="text-left px-3 py-2.5">Provider</th>
                <th className="text-left px-3 py-2.5">External ID</th>
                <th className="text-left px-3 py-2.5">Action</th>
                <th className="text-left px-3 py-2.5">Issues</th>
              </tr>
            </thead>
            <tbody>
              {preview.rows.map((r) => (
                <tr
                  key={r.row_no}
                  data-testid={`preview-row-${r.row_no}`}
                  className="border-b border-[#F8F7F2] last:border-b-0"
                >
                  <td className="px-3 py-2 text-[#9AA0A6]">{r.row_no}</td>
                  <td className="px-3 py-2 text-[#1F2937]">
                    {r.fields.first_name} {r.fields.last_name}
                    {r.full_name_split && (
                      <div
                        data-testid={`preview-row-${r.row_no}-fullname-source`}
                        className="text-[10px] text-[#9AA0A6] mt-0.5"
                      >
                        from “{r.full_name_split.source}”
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-[#6B7280]">
                    {r.fields.grade || "—"}
                  </td>
                  <td className="px-3 py-2 text-[#6B7280]">
                    {r.fields.school_name || "—"}
                    {r.default_school_applied && (
                      <div
                        data-testid={`preview-row-${r.row_no}-default-school`}
                        className="text-[10px] text-[#1E3F33] mt-0.5"
                      >
                        (defaulted)
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-[#6B7280]">
                    <span className="font-mono text-[11px]">
                      {r.fields.provider_email || (
                        r.provider_match_status === "solo_self" ? "(you)" : "—"
                      )}
                    </span>
                    {r.provider_match_status !== "none" && (
                      <span className="ml-1.5 text-[10px] uppercase tracking-[0.08em] text-[#9AA0A6]">
                        · {r.provider_match_status}
                      </span>
                    )}
                    {r.case_manager_match &&
                      (r.case_manager_match.matched || []).length > 0 && (
                        <div
                          data-testid={`preview-row-${r.row_no}-case-manager-match`}
                          className="text-[10px] text-[#1E3F33] mt-0.5"
                        >
                          CM:{" "}
                          {(r.case_manager_match.matched || [])
                            .map((m) => {
                              const isPrimary =
                                m.provider_id &&
                                m.provider_id ===
                                  r.case_manager_match.primary_provider_id;
                              const tag = isPrimary
                                ? " (Primary)"
                                : m.is_assistant
                                ? " (Asst)"
                                : m.match === "ambiguous"
                                ? " (Ambiguous)"
                                : m.match === "none"
                                ? " (Unmatched)"
                                : "";
                              return `${m.name}${tag}`;
                            })
                            .join(", ")}
                        </div>
                      )}
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px] text-[#6B7280]">
                    {r.fields.external_id || "—"}
                  </td>
                  <td className="px-3 py-2">
                    <ActionPill action={r.action} />
                    {r.match_reason && (
                      <div
                        data-testid={`preview-row-${r.row_no}-match-reason`}
                        className="text-[10px] text-[#9AA0A6] mt-0.5"
                      >
                        matched by{" "}
                        {MATCH_REASON_LABELS[r.match_reason] || r.match_reason}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-[11.5px]">
                    {r.identity_conflict && (
                      <div
                        data-testid={`preview-row-${r.row_no}-identity-conflict`}
                        className="text-[#7A3A2C] mb-1"
                      >
                        <div className="font-semibold">
                          Identity conflict — review required
                        </div>
                        <div>
                          Matched existing student{" "}
                          {r.identity_conflict.existing?.first_name}{" "}
                          {r.identity_conflict.existing?.last_name}
                          {r.identity_conflict.existing?.date_of_birth
                            ? ` (DOB ${r.identity_conflict.existing.date_of_birth})`
                            : ""}{" "}
                          by {MATCH_REASON_LABELS[r.match_reason] || "ID"}, but
                          this row is{" "}
                          {r.identity_conflict.incoming?.first_name}{" "}
                          {r.identity_conflict.incoming?.last_name}
                          {r.identity_conflict.incoming?.date_of_birth
                            ? ` (DOB ${r.identity_conflict.incoming.date_of_birth})`
                            : ""}
                          . This row will not be imported.
                        </div>
                      </div>
                    )}
                    {r.errors.map((e, i) => (
                      <div
                        key={`e-${i}`}
                        className="text-[#7A3A2C] flex items-start gap-1"
                      >
                        <XCircle
                          size={11}
                          weight="duotone"
                          className="mt-0.5"
                        />
                        <span>{typeof e === "string" ? e : (e.message || e.kind || JSON.stringify(e))}</span>
                      </div>
                    ))}
                    {r.warnings.map((w, i) => (
                      <div
                        key={`w-${i}`}
                        data-testid={
                          typeof w === "object" && w?.kind
                            ? `preview-row-${r.row_no}-warning-${w.kind}`
                            : undefined
                        }
                        className="text-[#7A6A2C] flex items-start gap-1"
                      >
                        <WarningCircle
                          size={11}
                          weight="duotone"
                          className="mt-0.5"
                        />
                        <span>
                          {typeof w === "string"
                            ? w
                            : (w?.message || w?.kind || JSON.stringify(w))}
                        </span>
                      </div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-4 mb-3">
        <OptCheckbox
          testId="opt-create-schools-2"
          checked={opts.allow_create_schools}
          onChange={(v) => setOpts((o) => ({ ...o, allow_create_schools: v }))}
        >
          Allow new schools / campuses
        </OptCheckbox>
        {!isSoloProvider && (
          <OptCheckbox
            testId="opt-invite-providers-2"
            checked={opts.invite_missing_providers}
            onChange={(v) =>
              setOpts((o) => ({ ...o, invite_missing_providers: v }))
            }
          >
            Invite providers from <code>provider_email</code> column
          </OptCheckbox>
        )}
        <OptCheckbox
          testId="opt-partial-import-2"
          checked={opts.allow_partial_import}
          onChange={(v) => setOpts((o) => ({ ...o, allow_partial_import: v }))}
        >
          Allow partial import (skip invalid rows)
        </OptCheckbox>
      </div>

      {error && (
        <div
          data-testid="csv-error"
          className="rounded-md border border-[#E8D6CD] bg-[#F8EFEA] text-[#7A3A2C] text-[12.5px] px-3 py-2 mb-3"
        >
          {error}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button
          onClick={onBack}
          variant="outline"
          className="h-9 px-4 text-[12.5px] border-[#E5E3DB] text-[#6B7280]"
        >
          Discard
        </Button>
        <Button
          data-testid="csv-commit-submit"
          onClick={onCommit}
          disabled={committing || commitBlocked}
          className="h-9 px-4 bg-[#1E3F33] hover:bg-[#173127] disabled:bg-[#1E3F33]/60 text-white text-[12.5px]"
        >
          {committing ? (
            <>
              <ArrowsClockwise size={12} className="mr-1.5 animate-spin" />
              Importing…
            </>
          ) : (
            <>Confirm import ({preview.summary?.valid_rows || 0})</>
          )}
        </Button>
      </div>
      {commitBlocked && (
        <p
          data-testid="csv-commit-blocked-note"
          className="text-right text-[11px] text-[#7A6A2C] mt-2"
        >
          {(preview.summary?.error_rows || 0) +
            (preview.summary?.identity_conflict_rows || 0)}{" "}
          row(s) are blocked (errors or identity conflicts). Fix the CSV or
          enable “Allow partial import” to commit valid rows only — blocked
          rows are never imported.
        </p>
      )}
    </>
  );
}

function SummaryTile({ label, value, tone }) {
  const tones = {
    ok: "bg-[#E8F0EB] text-[#1E3F33]",
    warn: "bg-[#F5EFE3] text-[#7A6A2C]",
    error: "bg-[#F8EFEA] text-[#7A3A2C]",
  };
  return (
    <div
      className={`rounded-md px-3 py-2.5 ${tones[tone] || "bg-[#F5F4ED] text-[#1F2937]"}`}
    >
      <div className="text-[10.5px] uppercase tracking-[0.1em] font-semibold opacity-80">
        {label}
      </div>
      <div className="text-[18px] font-semibold heading-font tracking-tight">
        {value ?? 0}
      </div>
    </div>
  );
}

function ResultView({ result, onAnother, isSoloProvider }) {
  const summary = useMemo(
    () => ({
      created: result.students_created?.length || 0,
      updated: result.students_updated?.length || 0,
      skipped: result.students_skipped?.length || 0,
      identityConflicts: result.identity_conflicts?.length || 0,
      assignments: result.assignments_created || 0,
      schools: result.schools_created?.length || 0,
      invites: result.invites_created?.length || 0,
      unassigned: result.unassigned_count || 0,
    }),
    [result]
  );

  return (
    <Card data-testid="csv-result-card" className="border-[#E5E3DB] rounded-2xl mb-6">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#E8F0EB] flex items-center justify-center">
            <CheckCircle size={22} weight="duotone" className="text-[#1E3F33]" />
          </div>
          <div>
            <h2 className="text-[16px] font-semibold text-[#1F2937] heading-font">
              Import complete
            </h2>
            <p className="text-[12.5px] text-[#6B7280]">
              {summary.created} created · {summary.updated} updated ·{" "}
              {summary.skipped} skipped
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-[12px]">
          <SummaryTile label="Students created" value={summary.created} tone="ok" />
          <SummaryTile label="Students updated" value={summary.updated} />
          <SummaryTile label="Students skipped" value={summary.skipped} />
          {summary.identityConflicts > 0 && (
            <SummaryTile
              label="Identity conflicts (not imported)"
              value={summary.identityConflicts}
              tone="error"
            />
          )}
          <SummaryTile label="Assignments" value={summary.assignments} />
          <SummaryTile label="New schools" value={summary.schools} />
          <SummaryTile label="Provider invites" value={summary.invites} />
          <SummaryTile
            label="Unassigned"
            value={summary.unassigned}
            tone={summary.unassigned > 0 ? "warn" : "ok"}
          />
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2 pt-3 border-t border-[#F1F2F4]">
          {!isSoloProvider && summary.unassigned > 0 && (
            <a
              data-testid="csv-result-assign-link"
              href="/admin/assignments?state=unassigned"
              className="text-[12.5px] text-[#1E3F33] underline decoration-[#8FA998]/60 underline-offset-[3px]"
            >
              Assign {summary.unassigned} student{summary.unassigned > 1 ? "s" : ""} →
            </a>
          )}
          {summary.invites > 0 && (
            <a
              href="/admin/access"
              className="text-[12.5px] text-[#1E3F33] underline decoration-[#8FA998]/60 underline-offset-[3px]"
            >
              View invites in Access & Invites →
            </a>
          )}
          <a
            href="/caseload"
            className="text-[12.5px] text-[#1E3F33] underline decoration-[#8FA998]/60 underline-offset-[3px]"
          >
            View caseload →
          </a>
          <Button
            data-testid="csv-import-another"
            onClick={onAnother}
            className="h-9 px-4 bg-[#1E3F33] hover:bg-[#173127] text-white text-[12.5px]"
          >
            Import another file
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function RecentImports({ batches, onRefresh }) {
  if (!batches?.length) {
    return (
      <Card
        data-testid="recent-imports-empty"
        className="border-[#E5E3DB] rounded-2xl"
      >
        <CardContent className="p-5 text-center text-[#6B7280] text-[12.5px]">
          <ClockClockwise size={16} weight="duotone" className="inline-block mr-1.5 -mt-0.5" />
          No recent imports yet. Your import history will appear here.
        </CardContent>
      </Card>
    );
  }
  return (
    <Card
      data-testid="recent-imports-card"
      className="border-[#E5E3DB] rounded-2xl"
    >
      <CardContent className="p-0">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#F1F2F4]">
          <h3 className="text-[12.5px] font-semibold text-[#1F2937] heading-font flex items-center gap-1.5">
            <ClockClockwise size={14} weight="duotone" className="text-[#3F4756]" />
            Recent imports
          </h3>
          <button
            data-testid="recent-imports-refresh"
            onClick={onRefresh}
            className="text-[11px] text-[#6B7280] hover:text-[#1F2937] inline-flex items-center gap-1"
          >
            <ArrowsClockwise size={11} /> Refresh
          </button>
        </div>
        <table className="w-full text-[12px]">
          <thead>
            <tr className="border-b border-[#F1F2F4] text-[10.5px] uppercase tracking-[0.1em] text-[#6B7280] font-semibold">
              <th className="text-left px-4 py-2">File</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-left px-4 py-2">Rows</th>
              <th className="text-left px-4 py-2">Result</th>
              <th className="text-left px-4 py-2">Previewed</th>
              <th className="text-left px-4 py-2">Committed</th>
            </tr>
          </thead>
          <tbody>
            {batches.map((b) => {
              const res = b.result;
              return (
                <tr
                  key={b.id}
                  data-testid={`recent-import-row-${b.id}`}
                  className="border-b border-[#F8F7F2] last:border-b-0"
                >
                  <td className="px-4 py-2.5">
                    <div className="text-[#1F2937]">{b.filename || "—"}</div>
                    {b.is_solo_import && (
                      <div className="text-[10px] uppercase tracking-[0.08em] text-[#3F4756] mt-0.5">
                        Solo
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      className="text-[10.5px] uppercase tracking-[0.08em] font-medium px-2 py-0.5 rounded-full"
                      style={
                        b.status === "committed"
                          ? { backgroundColor: "#E8F0EB", color: "#1E3F33" }
                          : b.status === "expired"
                          ? { backgroundColor: "#F8EFEA", color: "#7A3A2C" }
                          : { backgroundColor: "#EEF1F6", color: "#3F4756" }
                      }
                    >
                      {b.status}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-[#6B7280]">{b.row_count ?? "—"}</td>
                  <td className="px-4 py-2.5 text-[#6B7280]">
                    {res ? (
                      <span>
                        {res.students_created} created · {res.students_updated} updated
                        {res.students_skipped ? ` · ${res.students_skipped} skipped` : ""}
                        {res.unassigned_count
                          ? ` · ${res.unassigned_count} unassigned`
                          : ""}
                      </span>
                    ) : (
                      <span className="text-[#9AA0A6]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-[#6B7280]">{fmtDate(b.created_at)}</td>
                  <td className="px-4 py-2.5 text-[#6B7280]">
                    {b.committed_at ? fmtDate(b.committed_at) : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

// Tiny shim so RecentImports gets its ref to `ArrowRight` lint-free even
// if other parts of the file aren't using it. (No-op.)
void ArrowRight;

/**
 * FP3-056 — CSV column requirements panel.
 *
 * Rendered above the upload card on Import My Caseload (and the admin
 * variant) so the required / conditional / recommended-optional split
 * is obvious BEFORE the user downloads a template or picks a file.
 *
 * The expandable "View column guide" shows every supported column
 * with status (Required / Conditional / Optional), an example value
 * and a one-line note. Examples + minimum/recommended sample CSV
 * blocks use fake values only.
 */
const COLUMN_GUIDE_ROWS = [
  // Required ------------------------------------------------------------
  {
    name: "last_name",
    status: "required",
    example: "Sample",
    notes: "Family name. Used to identify and de-duplicate the student.",
  },
  {
    name: "first_name",
    status: "required",
    example: "Alex",
    notes: "Given name.",
  },
  {
    name: "grade",
    status: "required",
    example: "1",
    notes: "PK, K (or 0), 1–12.",
  },
  // Conditional ---------------------------------------------------------
  {
    name: "campus",
    status: "conditional",
    example: "Primary Workspace",
    notes:
      "Required only when your workspace has more than one school. " +
      "With a single workspace, leave blank to default to that school.",
    aliases: ["school", "school_name"],
  },
  // Recommended optional ------------------------------------------------
  {
    name: "external_student_id",
    status: "optional",
    example: "12345",
    notes:
      "Your SIS or district ID for this student. Lets re-imports update " +
      "the same record instead of creating a duplicate.",
    aliases: ["external_id", "student_id", "sis_id"],
  },
  {
    name: "student_email",
    status: "optional",
    example: "alex.sample@example.com",
    notes: "Optional student email. Stored as a secondary identifier.",
  },
  {
    name: "date_of_birth",
    status: "optional",
    example: "2018-04-12",
    notes: "YYYY-MM-DD only.",
    aliases: ["dob"],
  },
  {
    name: "sex",
    status: "optional",
    example: "female",
    notes: "female | male | other | not_specified.",
    aliases: ["gender"],
  },
  {
    name: "teacher",
    status: "optional",
    example: "Teacher Example",
    notes: "Homeroom or general-ed teacher name.",
    aliases: ["teacher_name", "homeroom_teacher"],
  },
  {
    name: "teacher_email",
    status: "optional",
    example: "teacher.example@example.com",
    notes: "Used by reports that ping the gen-ed teacher.",
  },
  {
    name: "attendance",
    status: "optional",
    example: "full_day",
    notes: "Attendance pattern key (e.g. full_day, half_day).",
    aliases: ["attendance_pattern"],
  },
  {
    name: "status",
    status: "optional",
    example: "active",
    notes: "Student status (default `active`).",
  },
  {
    name: "eligibility",
    status: "optional",
    example: "specific_learning_disability",
    notes:
      "Primary IDEA eligibility category. Matches the platform taxonomy.",
    aliases: ["primary_eligibility"],
  },
  {
    name: "lre",
    status: "optional",
    example: "B",
    notes: "Least-restrictive-environment code.",
  },
  {
    name: "iep_due_date",
    status: "optional",
    example: "2027-01-07",
    notes: "YYYY-MM-DD.",
  },
  {
    name: "met_due_date",
    status: "optional",
    example: "2028-01-27",
    notes: "Reevaluation due date. YYYY-MM-DD.",
    aliases: ["reevaluation_due_date"],
  },
  {
    name: "service_domain",
    status: "optional",
    example: "speech_language",
    notes:
      "One or more service domains, pipe-separated " +
      "(e.g. speech_language|academics).",
    aliases: ["service_domains", "domains"],
  },
  {
    name: "provider_email",
    status: "optional",
    example: "(your email)",
    notes:
      "Solo providers may leave blank — rows auto-assign to you. " +
      "If filled, it must match your account.",
  },
  {
    name: "provider_id",
    status: "optional",
    example: "(your provider id)",
    notes:
      "Solo providers may leave blank. If filled, it must match your account.",
  },
];

const STATUS_STYLES = {
  required: {
    bg: "bg-[#FBEFE8]",
    fg: "text-[#7A3A2C]",
    label: "Required",
  },
  conditional: {
    bg: "bg-[#F5EFE3]",
    fg: "text-[#7A6A2C]",
    label: "Sometimes required",
  },
  optional: {
    bg: "bg-[#E8F0EB]",
    fg: "text-[#1E3F33]",
    label: "Optional",
  },
};

function StatusPill({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.optional;
  return (
    <span
      className={`inline-flex items-center text-[10px] uppercase tracking-[0.08em] font-semibold px-1.5 py-0.5 rounded-full ${s.bg} ${s.fg}`}
    >
      {s.label}
    </span>
  );
}

function CsvColumnGuide({
  isSoloProvider,
  soloRequiresCampus,
  onDownloadTemplate,
}) {
  const required = COLUMN_GUIDE_ROWS.filter((c) => c.status === "required");
  const conditional = COLUMN_GUIDE_ROWS.filter((c) => c.status === "conditional");
  const recommended = COLUMN_GUIDE_ROWS.filter((c) => c.status === "optional").slice(0, 8);

  return (
    <Card
      data-testid="csv-column-guide"
      className="border-[#E5E3DB] rounded-2xl mb-4"
    >
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-2">
            <ListChecks
              size={20}
              weight="duotone"
              className="text-[#1E3F33] mt-0.5"
            />
            <div>
              <h2 className="text-[14px] font-semibold text-[#1F2937] heading-font tracking-tight">
                CSV column requirements
              </h2>
              <p className="text-[11.5px] text-[#6B7280] mt-0.5">
                Start with the template if you are unsure. Extra supported
                columns are optional and can be left blank.
              </p>
            </div>
          </div>
          <Button
            data-testid="csv-template-download-guide"
            onClick={onDownloadTemplate}
            variant="outline"
            className="h-8 text-[12px] border-[#E5E3DB] text-[#1E3F33] hover:bg-[#E8F0EB] shrink-0"
          >
            <Download size={12} className="mr-1.5" />
            Download template
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Required */}
          <div data-testid="csv-guide-required">
            <p className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-[#7A3A2C]">
              Required
            </p>
            <ul className="mt-1.5 space-y-1">
              {required.map((c) => (
                <li key={c.name} className="flex items-start gap-1.5">
                  <code className="text-[12px] text-[#1F2937] bg-[#F5F4ED] px-1.5 py-0.5 rounded">
                    {c.name}
                  </code>
                </li>
              ))}
            </ul>
          </div>

          {/* Conditional */}
          <div data-testid="csv-guide-conditional">
            <p className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-[#7A6A2C]">
              Sometimes required
            </p>
            <ul className="mt-1.5 space-y-1">
              {conditional.map((c) => (
                <li key={c.name} className="text-[12px] text-[#1F2937]">
                  <code className="bg-[#F5F4ED] px-1.5 py-0.5 rounded">
                    {c.name}
                  </code>
                  <span className="text-[#6B7280]"> — {c.notes}</span>
                </li>
              ))}
            </ul>
            {isSoloProvider && soloRequiresCampus && (
              <p
                data-testid="csv-guide-multi-school-warning"
                className="text-[11px] text-[#7A6A2C] bg-[#F5EFE3] border border-[#E8DCB8] rounded-md px-2 py-1.5 mt-2"
              >
                <strong>Heads up:</strong> your workspace has more than one
                school, so <code>campus</code> is required for every row.
              </p>
            )}
          </div>

          {/* Recommended optional */}
          <div data-testid="csv-guide-recommended">
            <p className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-[#1E3F33]">
              Recommended optional
            </p>
            <ul className="mt-1.5 space-y-1">
              {recommended.map((c) => (
                <li key={c.name} className="flex items-start gap-1.5">
                  <code className="text-[12px] text-[#1F2937] bg-[#F5F4ED] px-1.5 py-0.5 rounded">
                    {c.name}
                  </code>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Minimum / Recommended sample CSV blocks */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
          <CsvSample
            testid="csv-guide-sample-min"
            title="Minimum valid CSV"
            tone="ok"
            text={`last_name,first_name,grade
Sample,Alex,1`}
          />
          <CsvSample
            testid="csv-guide-sample-recommended"
            title="Recommended CSV"
            tone="info"
            text={`last_name,first_name,grade,campus,external_student_id,student_email,teacher,eligibility,iep_due_date,met_due_date
Sample,Alex,1,Primary Workspace,12345,alex.sample@example.com,Teacher Example,specific_learning_disability,2027-01-07,2028-01-27`}
          />
        </div>

        {/* Expandable full column guide */}
        <details
          data-testid="csv-guide-toggle"
          className="group rounded-md border border-[#E5E3DB] bg-[#FBFAF6] open:bg-white"
        >
          <summary
            className="cursor-pointer list-none px-3 py-2 text-[12px] font-medium text-[#1F2937] flex items-center justify-between"
            data-testid="csv-guide-toggle-summary"
          >
            <span className="flex items-center gap-1.5">
              <Info size={13} weight="duotone" className="text-[#1E3F33]" />
              View column guide ({COLUMN_GUIDE_ROWS.length} supported columns)
            </span>
            <CaretRight
              size={13}
              weight="bold"
              className="transition-transform group-open:rotate-90 text-[#6B7280]"
            />
          </summary>
          <div className="border-t border-[#E5E3DB] overflow-x-auto">
            <table
              className="w-full text-[12px]"
              data-testid="csv-guide-full-table"
            >
              <thead>
                <tr className="border-b border-[#F1F2F4] text-[10.5px] uppercase tracking-[0.08em] font-semibold text-[#6B7280]">
                  <th className="text-left px-3 py-2">Column</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Example</th>
                  <th className="text-left px-3 py-2">Notes &amp; aliases</th>
                </tr>
              </thead>
              <tbody>
                {COLUMN_GUIDE_ROWS.map((c) => (
                  <tr
                    key={c.name}
                    data-testid={`csv-guide-row-${c.name}`}
                    className="border-b border-[#F8F7F2] last:border-b-0"
                  >
                    <td className="px-3 py-2 align-top">
                      <code className="text-[12px] text-[#1F2937] bg-[#F5F4ED] px-1.5 py-0.5 rounded">
                        {c.name}
                      </code>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <StatusPill status={c.status} />
                    </td>
                    <td className="px-3 py-2 align-top text-[#3F4756] font-mono text-[11px]">
                      {c.example}
                    </td>
                    <td className="px-3 py-2 align-top text-[11.5px] text-[#6B7280]">
                      {c.notes}
                      {c.aliases?.length ? (
                        <div className="text-[10.5px] text-[#9AA0A6] mt-0.5">
                          Also accepts:{" "}
                          {c.aliases.map((a, i) => (
                            <span key={a}>
                              <code>{a}</code>
                              {i < c.aliases.length - 1 ? ", " : ""}
                            </span>
                          ))}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      </CardContent>
    </Card>
  );
}

function CsvSample({ title, text, testid, tone }) {
  const toneCls = tone === "ok"
    ? "border-[#CDE3D5] bg-[#F1F8F3]"
    : "border-[#E5E3DB] bg-[#FBFAF6]";
  return (
    <div
      data-testid={testid}
      className={`rounded-md border ${toneCls} p-3`}
    >
      <p className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-[#3F4756] mb-1.5">
        {title}
      </p>
      <pre className="text-[11px] font-mono text-[#1F2937] whitespace-pre-wrap break-all">
        {text}
      </pre>
    </div>
  );
}
